// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PlanType } from '@/account/api/types';
import {
  ArrowForward,
  Check,
  RocketLaunch,
} from '@/constants/hardfin-standard-icons';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SvgIconProps,
  Typography,
} from '@mui/material';

interface PlanFeature {
  title: string;
  description: string;
}

interface Plan {
  title: string;
  features: PlanFeature[];
  iconColor: SvgIconProps['color'];
}

type Plans = Record<PlanType, Plan>;

const plans: Plans = {
  FREE: {
    title: 'Free plan',
    features: [
      { title: 'Users', description: 'One free user account' },
      { title: 'Assets', description: 'Track up to 10 active assets' },
      { title: 'Projects', description: 'Track up to 10 active projects' },
      { title: 'Storage', description: 'Upload up to 100 attachments' },
      { title: 'Shipments', description: 'Record shipping data manually' },
      { title: 'Exports', description: 'Standard CSV data exports' },
      { title: 'Integrations', description: 'Receive email notifications' },
      { title: 'Support', description: 'Email support during business hours' },
      { title: 'Change history', description: '30 days of audit logs' },
    ],
    iconColor: 'action',
  },
  PAID: {
    title: 'Paid plans',
    features: [
      { title: 'Users', description: 'Unlimited active users' },
      { title: 'Assets', description: 'Unlimited active assets' },
      { title: 'Projects', description: 'Unlimited active projects' },
      {
        title: 'Storage',
        description: 'Unlimited project and file attachments',
      },
      {
        title: 'Shipments',
        description: 'Track carrier shipments automatically',
      },
      { title: 'Exports', description: 'Advanced data exports and API' },
      {
        title: 'Integrations',
        description: 'Connect your tools (Slack, CRM, etc)',
      },
      {
        title: 'Support',
        description: '24/7 priority support',
      },
      { title: 'Change history', description: 'Unlimited audit logs' },
    ],
    iconColor: 'primary',
  },
};

interface PlanListProps {
  /**
   * The type of plan to display features for
   */
  planType: PlanType;
}

/**
 * Renders a list of features for a given plan (Free or Pro).
 */
function PlanList(props: PlanListProps) {
  const plan = plans[props.planType];

  return (
    <Box
      sx={{
        paddingX: 2,
        paddingY: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="h6" align="center" color={plan.iconColor}>
        {plan.title}
      </Typography>
      <List dense>
        {plan.features.map((feature, index) => (
          <ListItem key={index} sx={{ padding: 0 }}>
            <ListItemIcon>
              <Check color={plan.iconColor} />
            </ListItemIcon>
            <ListItemText
              primary={feature.title}
              secondary={feature.description}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

interface Props {
  /**
   * If true, the modal is visible
   */
  isOpen: boolean;

  /**
   * A callback fired when the modal requests to be closed
   */
  onClose: () => void;
}

/**
 * A modal component that displays plan comparison information and allows users to upgrade.
 * Shows feature lists for both Free and Pro plans side-by-side, with a call-to-action
 * button to upgrade. Used primarily from the PlanStatusPill component when users click
 * to learn more about upgrading.
 */
export function UpgradeModal(props: Props) {
  return (
    <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="md" fullWidth>
      <DialogTitle display="flex" alignItems="center" gap={1}>
        <RocketLaunch color="primary" />
        You&rsquo;re already enjoying the benefits of Hardfin
      </DialogTitle>
      <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
        Upgrade to unlock unlimited projects, advanced analytics, priority
        support, and complete audit logs!
      </Typography>
      <DialogContent>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            sm: '1fr 4fr 1fr 4fr 1fr',
          }}
          gap={2}
          alignItems="center"
        >
          <Box />
          <Box>
            <PlanList planType="FREE" />
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <ArrowForward color="primary" sx={{ fontSize: 40 }} />
          </Box>
          <Box>
            <PlanList planType="PAID" />
          </Box>
          <Box />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          href="https://www.hardfin.com/contact"
          target="_blank"
        >
          Contact us to upgrade
        </Button>
      </DialogActions>
    </Dialog>
  );
}
